import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  entities,
  qr,
  sales,
  settings,
  media,
  tags as tagsApi,
  marketplace,
} from "../services/api";
import {
  BackIcon,
  PencilIcon,
  CloseIcon,
  ChevronRightIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DocumentIcon,
  LinkIcon,
  ShareIcon,
  DownloadIcon,
} from "../components/Icons";
import OfferUpListingModal from "../components/OfferUpListingModal";
import FacebookListingModal from "../components/FacebookListingModal";
import EbayListingModal from "../components/EbayListingModal";
import MarketplaceSelectorModal from "../components/MarketplaceSelectorModal";
import TagMultiSelect from "../components/TagMultiSelect";
import SearchableSelect from "../components/SearchableSelect";
import MediaManager from "../components/MediaManager";
import { toast } from "../components/Toast";
import { formatDate, formatDateForInput } from "../utils/dateUtils";
import {
  downloadFile,
  productExportText,
  slugify,
} from "../utils/exportUtils";

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;

function EntityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [children, setChildren] = useState([]);
  const [parent, setParent] = useState(null);
  const [availableParents, setAvailableParents] = useState([]);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showOfferUpModal, setShowOfferUpModal] = useState(false);
  const [showFacebookModal, setShowFacebookModal] = useState(false);
  const [showEbayModal, setShowEbayModal] = useState(false);
  const [showMarketplaceSelector, setShowMarketplaceSelector] = useState(false);
  const [offerupListing, setOfferupListing] = useState(null);
  const [facebookListing, setFacebookListing] = useState(null);
  const [ebayListing, setEbayListing] = useState(null);
  const [salePrice, setSalePrice] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFirstSaleCongrats, setShowFirstSaleCongrats] = useState(false);

  // Inline editing state - common fields
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editParentId, setEditParentId] = useState("");

  // Product fields
  const [editSku, setEditSku] = useState("");
  const [editPurchasePrice, setEditPurchasePrice] = useState("");
  const [editRetailPrice, setEditRetailPrice] = useState("");
  const [editMinSalePrice, setEditMinSalePrice] = useState("");
  const [editListSalePrice, setEditListSalePrice] = useState("");
  const [editQuantity, setEditQuantity] = useState("");
  const [editCondition, setEditCondition] = useState("");
  const [editPurchaseDate, setEditPurchaseDate] = useState("");
  const [editMyReview, setEditMyReview] = useState("");
  const [editMyRating, setEditMyRating] = useState("");
  const [editVineReviewed, setEditVineReviewed] = useState(false);

  // Container fields
  const [editCapacity, setEditCapacity] = useState("");
  const [editContainerType, setEditContainerType] = useState("");

  // Location fields
  const [editAddress, setEditAddress] = useState("");
  const [editLocationType, setEditLocationType] = useState("");

  // QR code field
  const [editQrCode, setEditQrCode] = useState("");

  // Settings-based options
  const [containerTypes, setContainerTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);

  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Undo state for removed children
  const [removedChildren, setRemovedChildren] = useState([]);

  // Media state
  const [mediaItems, setMediaItems] = useState([]);
  const [displayedMediaId, setDisplayedMediaId] = useState(null);

  // QR URL copy feedback
  const [copiedQrUrl, setCopiedQrUrl] = useState(false);
  const [copiedListingUrl, setCopiedListingUrl] = useState(null);

  // Guards against a slow response for a previous entity landing after a
  // newer one and overwriting the current entity's state
  const entityRequestIdRef = useRef(0);

  useEffect(() => {
    // Reset per-entity UI state when navigating between entities
    setRemovedChildren([]);
    setIsEditing(false);
    setDisplayedMediaId(null);
    setCopiedQrUrl(false);
    setSalePrice("");
    setOfferupListing(null);
    setFacebookListing(null);
    setEbayListing(null);
    loadEntity();
    loadSettings();
    loadAllTags();
    loadMedia();
  }, [id]);

  const loadMedia = async () => {
    try {
      const response = await media.list(id);
      setMediaItems(response.data?.data || []);
    } catch (err) {
      console.error("Failed to load media:", err);
      setMediaItems([]);
    }
  };

  const loadAllTags = async () => {
    try {
      const { data } = await tagsApi.list();
      setAllTags(data);
    } catch (err) {
      console.error("Failed to load tags:", err);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await settings.get();
      setContainerTypes(data.containerTypes || []);
      setLocationTypes(data.locationTypes || []);
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  const loadEntity = async () => {
    const requestId = ++entityRequestIdRef.current;
    const isStale = () => requestId !== entityRequestIdRef.current;
    setLoading(true);
    try {
      const { data } = await entities.get(id);
      if (isStale()) return;
      setEntity(data);

      // Set edit state for common fields
      setEditName(data.name);
      setEditDescription(data.description || "");
      setEditUrl(data.url || "");
      setEditParentId(data.parent_id || "none");

      // Set type-specific fields
      if (data.entity_type === "product") {
        setEditSku(data.sku || "");
        setEditPurchasePrice(data.purchase_price?.toString() || "");
        setEditRetailPrice(data.retail_price?.toString() || "");
        setEditMinSalePrice(data.min_sale_price?.toString() || "");
        setEditListSalePrice(data.list_sale_price?.toString() || "");
        setEditQuantity(data.quantity?.toString() || "1");
        setEditCondition(data.condition || "");
        setEditPurchaseDate(formatDateForInput(data.purchase_date));
        setEditMyReview(data.my_review || "");
        setEditMyRating(data.my_rating?.toString() || "");
        setEditVineReviewed(!!data.vine_reviewed_at);
        if (data.list_sale_price) {
          setSalePrice(data.list_sale_price.toString());
        }

        // Load marketplace listings if exist
        try {
          const [offerupRes, facebookRes, ebayRes] = await Promise.all([
            marketplace
              .getByEntityAndMarketplace(id, "offerup")
              .catch(() => null),
            marketplace
              .getByEntityAndMarketplace(id, "facebook")
              .catch(() => null),
            marketplace.getByEntityAndMarketplace(id, "ebay").catch(() => null),
          ]);
          if (isStale()) return;
          setOfferupListing(offerupRes?.data?.data || null);
          setFacebookListing(facebookRes?.data?.data || null);
          setEbayListing(ebayRes?.data?.data || null);
        } catch (err) {
          setOfferupListing(null);
          setFacebookListing(null);
          setEbayListing(null);
        }
      } else if (data.entity_type === "container") {
        setEditCapacity(data.capacity?.toString() || "");
        setEditContainerType(data.container_type || "box");
      } else if (data.entity_type === "location") {
        setEditAddress(data.address || "");
        setEditLocationType(data.location_type || "warehouse");
      }

      // Set tags
      setSelectedTagIds(data.tags?.map((t) => t.id) || []);

      // Set QR code
      setEditQrCode(data.qr_code || "");

      // Load parent options now that we know the entity's type
      loadAvailableParents(data.entity_type);

      // Load children
      const childrenRes = await entities.list({ parent_id: id, limit: -1 });
      if (isStale()) return;
      setChildren(childrenRes.data || []);

      // Load parent if exists
      if (data.parent_id) {
        try {
          const parentRes = await entities.get(data.parent_id);
          if (isStale()) return;
          setParent(parentRes.data);
        } catch (err) {
          console.error("Failed to load parent:", err);
        }
      } else {
        setParent(null);
      }
    } catch (err) {
      console.error("Failed to load entity:", err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const loadAvailableParents = async (entityType) => {
    try {
      // Type rules: products go in containers or locations; containers and
      // locations go in locations. Filter server-side, unlimited.
      const types =
        entityType === "product" ? ["container", "location"] : ["location"];
      const results = await Promise.all(
        types.map((t) => entities.list({ entity_type: t, limit: -1 })),
      );
      const filtered = results
        .flatMap((r) => r.data || [])
        .filter((e) => e.id !== id);

      setAvailableParents(filtered);
    } catch (err) {
      console.error("Failed to load available parents:", err);
    }
  };

  // True when edit mode holds changes the user would lose by leaving
  const hasUnsavedChanges = () => {
    if (!entity || !isEditing) return false;
    if (editName !== entity.name) return true;
    if (editDescription !== (entity.description || "")) return true;
    if (editUrl !== (entity.url || "")) return true;
    if (editParentId !== (entity.parent_id || "none")) return true;
    if (editQrCode !== (entity.qr_code || "")) return true;
    const entityTagIds = (entity.tags || []).map((t) => t.id);
    if (
      selectedTagIds.length !== entityTagIds.length ||
      selectedTagIds.some((tagId) => !entityTagIds.includes(tagId))
    ) {
      return true;
    }
    if (entity.entity_type === "product") {
      return (
        editSku !== (entity.sku || "") ||
        editPurchasePrice !== (entity.purchase_price?.toString() || "") ||
        editRetailPrice !== (entity.retail_price?.toString() || "") ||
        editMinSalePrice !== (entity.min_sale_price?.toString() || "") ||
        editListSalePrice !== (entity.list_sale_price?.toString() || "") ||
        editQuantity !== (entity.quantity?.toString() || "1") ||
        editCondition !== (entity.condition || "") ||
        editPurchaseDate !== formatDateForInput(entity.purchase_date) ||
        editMyReview !== (entity.my_review || "") ||
        editMyRating !== (entity.my_rating?.toString() || "") ||
        editVineReviewed !== !!entity.vine_reviewed_at
      );
    }
    if (entity.entity_type === "container") {
      return (
        editCapacity !== (entity.capacity?.toString() || "") ||
        editContainerType !== (entity.container_type || "box")
      );
    }
    if (entity.entity_type === "location") {
      return (
        editAddress !== (entity.address || "") ||
        editLocationType !== (entity.location_type || "warehouse")
      );
    }
    return false;
  };

  // Warn before the browser unloads the page with unsaved edits
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  const handleSave = async () => {
    try {
      const updates = {
        name: editName,
        description: editDescription,
        url: editUrl || null,
      };

      // Add type-specific fields
      if (entity.entity_type === "product") {
        updates.sku = editSku || null;
        updates.purchase_price = editPurchasePrice
          ? parseFloat(editPurchasePrice)
          : null;
        updates.retail_price = editRetailPrice
          ? parseFloat(editRetailPrice)
          : null;
        updates.min_sale_price = editMinSalePrice
          ? parseFloat(editMinSalePrice)
          : null;
        updates.list_sale_price = editListSalePrice
          ? parseFloat(editListSalePrice)
          : null;
        updates.quantity = editQuantity ? parseInt(editQuantity, 10) : null;
        updates.condition = editCondition || null;
        updates.purchase_date = editPurchaseDate || null;
        updates.my_review = editMyReview || null;
        updates.my_rating = editMyRating ? parseInt(editMyRating, 10) : null;
        updates.vine_reviewed_at = editVineReviewed
          ? entity.vine_reviewed_at || new Date().toISOString()
          : null;
      } else if (entity.entity_type === "container") {
        updates.capacity = editCapacity ? parseInt(editCapacity, 10) : null;
        updates.container_type = editContainerType || null;
      } else if (entity.entity_type === "location") {
        updates.address = editAddress || null;
        updates.location_type = editLocationType || null;
      }

      await entities.update(id, updates);

      // Update parent if changed
      if (editParentId !== (entity.parent_id || "none")) {
        const newParentId = editParentId === "none" ? null : editParentId;
        await entities.move(id, newParentId);
      }

      // Update tags
      await entities.setTags(id, selectedTagIds);

      // Update QR code if changed
      if (editQrCode !== (entity.qr_code || "")) {
        await entities.assignQrCode(id, editQrCode || null);
      }

      setIsEditing(false);
      loadEntity();
    } catch (err) {
      console.error("Failed to update entity:", err);
      toast.error(err.response?.data?.error || "Failed to update entity");
    }
  };

  const handleCancel = () => {
    setEditName(entity.name);
    setEditDescription(entity.description || "");
    setEditUrl(entity.url || "");
    setEditParentId(entity.parent_id || "none");

    if (entity.entity_type === "product") {
      setEditSku(entity.sku || "");
      setEditPurchasePrice(entity.purchase_price?.toString() || "");
      setEditRetailPrice(entity.retail_price?.toString() || "");
      setEditMinSalePrice(entity.min_sale_price?.toString() || "");
      setEditListSalePrice(entity.list_sale_price?.toString() || "");
      setEditQuantity(entity.quantity?.toString() || "1");
      setEditCondition(entity.condition || "");
      setEditPurchaseDate(formatDateForInput(entity.purchase_date));
      setEditMyReview(entity.my_review || "");
      setEditMyRating(entity.my_rating?.toString() || "");
      setEditVineReviewed(!!entity.vine_reviewed_at);
    } else if (entity.entity_type === "container") {
      setEditCapacity(entity.capacity?.toString() || "");
      setEditContainerType(entity.container_type || "box");
    } else if (entity.entity_type === "location") {
      setEditAddress(entity.address || "");
      setEditLocationType(entity.location_type || "warehouse");
    }

    setSelectedTagIds(entity.tags?.map((t) => t.id) || []);
    setEditQrCode(entity.qr_code || "");
    setIsEditing(false);
  };

  const fireConfetti = () => {
    // Fire confetti from multiple directions for a festive effect
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ["#00d498", "#1877F2", "#FFD700", "#FF6B6B", "#4ECDC4"],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ["#00d498", "#1877F2", "#FFD700", "#FF6B6B", "#4ECDC4"],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#00d498", "#1877F2", "#FFD700", "#FF6B6B", "#4ECDC4"],
    });

    frame();
  };

  const parsedSalePrice = parseFloat(salePrice);
  const salePriceValid = Number.isFinite(parsedSalePrice) && parsedSalePrice > 0;

  const handleSell = async () => {
    if (!salePriceValid) {
      toast.error("Enter a valid sale price");
      return;
    }
    try {
      // Check if this is the first sale
      const { data: statsData } = await sales.stats();
      const isFirstSale = (statsData?.totalSales || 0) === 0;

      await sales.create({
        entity_id: id,
        sale_price: parsedSalePrice,
        purchase_price: entity.purchase_price || 0,
      });

      if (isFirstSale) {
        // Fire confetti and show congratulations modal
        setShowSellModal(false);
        fireConfetti();
        setShowFirstSaleCongrats(true);
      } else {
        // Stay on the entity so the sold state is visible
        setShowSellModal(false);
        loadEntity();
      }
    } catch (err) {
      console.error("Failed to record sale:", err);
      toast.error(err.response?.data?.error || "Failed to record sale");
    }
  };

  const handleDelete = async () => {
    try {
      await entities.delete(id);
      // Navigate back to the appropriate list page
      const listPages = {
        product: "/products",
        container: "/containers",
        location: "/locations",
      };
      navigate(listPages[entity.entity_type] || "/");
    } catch (err) {
      console.error("Failed to delete:", err);
      toast.error("Failed to delete");
    }
  };

  const handleRemoveChild = async (child, e) => {
    e.stopPropagation();

    try {
      // Move child to no parent
      await entities.move(child.id, null);

      // Remove from local state
      setChildren((prev) => prev.filter((c) => c.id !== child.id));

      // Add to removed list for undo
      setRemovedChildren((prev) => [...prev, child]);
    } catch (err) {
      console.error("Failed to remove item:", err);
      toast.error("Failed to remove item");
    }
  };

  const handleUndoRemove = async (childToRestore) => {
    try {
      // Move child back to this parent
      await entities.move(childToRestore.id, id);

      // Add back to children list
      setChildren((prev) => [...prev, childToRestore]);

      // Remove from undo list
      setRemovedChildren((prev) =>
        prev.filter((c) => c.id !== childToRestore.id),
      );
    } catch (err) {
      console.error("Failed to undo:", err);
      toast.error("Failed to undo");
    }
  };

  const handleUndoAll = async () => {
    try {
      // Restore all removed children
      for (const child of removedChildren) {
        await entities.move(child.id, id);
      }

      // Add all back to children list
      setChildren((prev) => [...prev, ...removedChildren]);

      // Clear undo list
      setRemovedChildren([]);
    } catch (err) {
      console.error("Failed to undo:", err);
      toast.error("Failed to undo");
    }
  };

  const getEntityIcon = (type, size = 20) => {
    switch (type) {
      case "product":
        return <ProductIcon size={size} />;
      case "container":
        return <ContainerIcon size={size} />;
      case "location":
        return <LocationIcon size={size} />;
      default:
        return <DocumentIcon size={size} />;
    }
  };

  const getEntityColor = (type) => {
    switch (type) {
      case "product":
        return "badge-product";
      case "container":
        return "badge-container";
      case "location":
        return "badge-location";
      default:
        return "badge-product";
    }
  };

  const formatCondition = (condition) => {
    const labels = {
      new: "New",
      like_new: "Like New",
      good: "Good",
      fair: "Fair",
      poor: "Poor",
    };
    return labels[condition] || condition;
  };

  const formatType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " ");
  };

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <h2>Entity not found</h2>
        <button onClick={() => navigate("/")} className="btn btn-primary mt-3">
          Go Home
        </button>
      </div>
    );
  }

  const canHaveParent =
    entity.entity_type === "product" || entity.entity_type === "container";
  const isTrashed = !!entity.deleted_at;
  const isSold = entity.status === "sold";
  const displayedMedia =
    mediaItems.find((m) => m.id === displayedMediaId) ||
    mediaItems.find((m) => m.is_primary) ||
    mediaItems[0];

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => {
              if (
                hasUnsavedChanges() &&
                !window.confirm("Discard unsaved changes?")
              ) {
                return;
              }
              navigate(-1);
            }}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">{entity.name}</h1>
            <p className="header-subtitle">
              {entity.entity_type.charAt(0).toUpperCase() +
                entity.entity_type.slice(1)}
              {entity.qr_code && ` - ${entity.qr_code}`}
            </p>
          </div>
          {!isEditing && (
            <div className="header-actions-right">
              {entity.entity_type === "product" &&
                /^[A-Z0-9]{10}$/.test(entity.sku || "") && (
                  <a
                    href={`https://www.amazon.com/review/create-review?encoding=UTF&channel=vine-portal&asin=${entity.sku}#fs-fill`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={async () => {
                      if (entity.vine_reviewed_at) return;
                      try {
                        await entities.update(id, {
                          vine_reviewed_at: new Date().toISOString(),
                        });
                        loadEntity();
                      } catch (err) {
                        console.error("Failed to mark Vine review:", err);
                      }
                    }}
                    className="btn btn-secondary btn-sm"
                    style={{
                      alignSelf: "center",
                      ...(entity.vine_reviewed_at
                        ? { color: "var(--success, #00d498)" }
                        : {}),
                    }}
                    title={
                      entity.vine_reviewed_at
                        ? `Vine review marked done ${formatDate(entity.vine_reviewed_at)} — click to reopen the form (untick "Reviewed on Vine" in Edit to reset)`
                        : "Open the Amazon Vine review form (auto-fills from My Review) and mark this product as reviewed"
                    }
                  >
                    {entity.vine_reviewed_at ? "Vine ✓" : "Vine Review"}
                  </a>
                )}
              {entity.entity_type === "product" && (
                <button
                  onClick={() =>
                    downloadFile(
                      `${slugify(entity.name)}.txt`,
                      productExportText(entity),
                    )
                  }
                  className="btn btn-ghost btn-icon"
                  title="Export title, description & reviews"
                >
                  <DownloadIcon size={20} />
                </button>
              )}
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-ghost btn-icon"
                title="Edit"
              >
                <PencilIcon size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        {/* Status banners */}
        {isTrashed && (
          <div
            className="card"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--spacing-sm)",
              padding: "var(--spacing-md)",
              borderLeft: "4px solid var(--danger)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            <span style={{ fontWeight: 500 }}>This item is in the trash.</span>
            <button
              onClick={() => navigate("/trash")}
              className="btn btn-secondary btn-sm"
            >
              Open Trash
            </button>
          </div>
        )}
        {!isTrashed && isSold && (
          <div
            className="card"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--spacing-sm)",
              padding: "var(--spacing-md)",
              borderLeft: "4px solid var(--success)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            <span style={{ fontWeight: 500 }}>This item has been sold.</span>
            <button
              onClick={() => navigate("/sales/sold")}
              className="btn btn-secondary btn-sm"
            >
              View Sales
            </button>
          </div>
        )}

        {/* Edit Form */}
        {isEditing && (
          <div className="card">
            <label>Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="mb-2"
              autoFocus
            />

            <label>Description</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows="3"
              className="mb-2"
            />

            {entity.entity_type === "product" && (
              <>
                <label>My Review</label>
                <textarea
                  value={editMyReview}
                  onChange={(e) => setEditMyReview(e.target.value)}
                  rows="3"
                  placeholder="Your own review of this product"
                  className="mb-2"
                />
                <label>My Rating</label>
                <select
                  value={editMyRating}
                  onChange={(e) => setEditMyRating(e.target.value)}
                  className="mb-2"
                >
                  <option value="">No rating</option>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {"★".repeat(n)}
                      {"☆".repeat(5 - n)} ({n})
                    </option>
                  ))}
                </select>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-xs)",
                  }}
                  className="mb-2"
                >
                  <input
                    type="checkbox"
                    checked={editVineReviewed}
                    onChange={(e) => setEditVineReviewed(e.target.checked)}
                    style={{ width: "auto", margin: 0 }}
                  />
                  Reviewed on Vine
                </label>
              </>
            )}

            <label>URL</label>
            <input
              type="url"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="https://..."
              className="mb-2"
            />

            {canHaveParent && (
              <>
                <label>Location</label>
                <SearchableSelect
                  options={[
                    { id: "none", name: "None (Top Level)" },
                    ...availableParents,
                  ]}
                  value={editParentId}
                  onChange={(val) => setEditParentId(val || "none")}
                  searchable
                  getOptionLabel={(opt) => opt.name}
                  getOptionValue={(opt) => opt.id}
                />
              </>
            )}

            {/* Product-specific edit fields */}
            {entity.entity_type === "product" && (
              <>
                <label>SKU / Barcode</label>
                <input
                  type="text"
                  value={editSku}
                  onChange={(e) => setEditSku(e.target.value)}
                  className="mb-2"
                />

                <div className="grid grid-2">
                  <div>
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={editQuantity}
                      onChange={(e) => setEditQuantity(e.target.value)}
                      min="0"
                    />
                  </div>
                  <div>
                    <label>Condition</label>
                    <SearchableSelect
                      options={[
                        { value: "", label: "" },
                        { value: "new", label: "New" },
                        { value: "like_new", label: "Like New" },
                        { value: "good", label: "Good" },
                        { value: "fair", label: "Fair" },
                        { value: "poor", label: "Poor" },
                      ]}
                      value={editCondition}
                      onChange={(val) => setEditCondition(val || "")}
                    />
                  </div>
                </div>

                <div className="grid grid-2 mt-2">
                  <div>
                    <label>Purchase Date</label>
                    <input
                      type="date"
                      value={editPurchaseDate}
                      onChange={(e) => setEditPurchaseDate(e.target.value)}
                    />
                  </div>
                  <div></div>
                </div>

                <div className="grid grid-2 mt-2">
                  <div>
                    <label>Purchase Price</label>
                    <input
                      type="number"
                      value={editPurchasePrice}
                      onChange={(e) => setEditPurchasePrice(e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label>Retail Price</label>
                    <input
                      type="number"
                      value={editRetailPrice}
                      onChange={(e) => setEditRetailPrice(e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-2 mt-2">
                  <div>
                    <label>Minimum Sale Price</label>
                    <input
                      type="number"
                      value={editMinSalePrice}
                      onChange={(e) => setEditMinSalePrice(e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label>List Sale Price</label>
                    <input
                      type="number"
                      value={editListSalePrice}
                      onChange={(e) => setEditListSalePrice(e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Container-specific edit fields */}
            {entity.entity_type === "container" && (
              <div className="grid grid-2">
                <div>
                  <label>Container Type</label>
                  <SearchableSelect
                    options={containerTypes.map((type) => ({
                      value: type,
                      label: formatType(type),
                    }))}
                    value={editContainerType}
                    onChange={(val) => setEditContainerType(val)}
                  />
                </div>
                <div>
                  <label>Capacity</label>
                  <input
                    type="number"
                    value={editCapacity}
                    onChange={(e) => setEditCapacity(e.target.value)}
                    min="0"
                  />
                </div>
              </div>
            )}

            {/* Location-specific edit fields */}
            {entity.entity_type === "location" && (
              <>
                <label>Location Type</label>
                <SearchableSelect
                  options={locationTypes.map((type) => ({
                    value: type,
                    label: formatType(type),
                  }))}
                  value={editLocationType}
                  onChange={(val) => setEditLocationType(val)}
                />

                <label style={{ marginTop: "var(--spacing-sm)" }}>
                  Address
                </label>
                <textarea
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  rows="2"
                />
              </>
            )}

            {/* Tags */}
            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>Tags</label>
              <TagMultiSelect
                allTags={allTags}
                selectedTagIds={selectedTagIds}
                onTagsChange={setSelectedTagIds}
                onCreateTag={async (name) => {
                  const { data } = await tagsApi.create({ name });
                  setAllTags([...allTags, data]);
                  return data;
                }}
              />
            </div>

            {/* QR Code */}
            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>QR Code</label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-sm)",
                }}
              >
                <input
                  type="text"
                  value={editQrCode}
                  onChange={(e) => setEditQrCode(e.target.value)}
                  placeholder="Enter or scan QR code"
                  style={{ flex: 1 }}
                />
                {editQrCode && (
                  <button
                    type="button"
                    onClick={() => setEditQrCode("")}
                    className="btn btn-ghost btn-sm"
                    title="Clear QR code"
                  >
                    <CloseIcon size={16} />
                  </button>
                )}
              </div>
              {entity.qr_code && editQrCode === entity.qr_code && (
                <div
                  style={{
                    marginTop: "var(--spacing-xs)",
                    fontSize: "0.75rem",
                    color: "var(--text-tertiary)",
                  }}
                >
                  Current QR code assigned
                </div>
              )}
              {editQrCode && editQrCode !== entity.qr_code && (
                <div
                  style={{
                    marginTop: "var(--spacing-xs)",
                    fontSize: "0.75rem",
                    color: "var(--accent-primary)",
                  }}
                >
                  Will link new QR code on save
                </div>
              )}
              {!editQrCode && entity.qr_code && (
                <div
                  style={{
                    marginTop: "var(--spacing-xs)",
                    fontSize: "0.75rem",
                    color: "var(--warning)",
                  }}
                >
                  Will unlink QR code on save
                </div>
              )}
            </div>

            {/* Media Manager */}
            <div style={{ marginTop: "var(--spacing-md)" }}>
              <MediaManager
                entityId={id}
                mediaItems={mediaItems}
                onMediaChange={loadMedia}
              />
            </div>

            <div className="flex gap-2 mt-3">
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn btn-success"
                style={{ flex: 1 }}
              >
                Save
              </button>
            </div>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-danger"
              style={{ width: "100%", marginTop: "var(--spacing-lg)" }}
            >
              Delete
            </button>
          </div>
        )}

        {/* Main Content Card */}
        {!isEditing && (
          <div className="card">
            {/* Product Images */}
            {mediaItems.length > 0 && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                {/* Displayed image large */}
                {displayedMedia && (
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom:
                        mediaItems.length > 1 ? "var(--spacing-sm)" : 0,
                    }}
                  >
                    <img
                      src={media.getImageUrl(displayedMedia.file_path)}
                      alt={entity.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        objectFit: "contain",
                        borderRadius: "var(--radius-md)",
                        background: "var(--bg-secondary)",
                      }}
                    />
                  </div>
                )}
                {/* Thumbnail gallery for additional images */}
                {mediaItems.length > 1 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--spacing-xs)",
                      overflowX: "auto",
                      paddingBottom: "var(--spacing-xs)",
                    }}
                  >
                    {mediaItems.map((item) => (
                      <img
                        key={item.id}
                        src={media.getImageUrl(item.file_path)}
                        alt=""
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "var(--radius-sm)",
                          border:
                            item.id === displayedMedia?.id
                              ? "2px solid var(--accent-primary)"
                              : "1px solid var(--border-color)",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        onClick={() => setDisplayedMediaId(item.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {entity.description && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">Description</div>
                <p className="text-secondary" style={{ margin: 0 }}>
                  {entity.description}
                </p>
              </div>
            )}

            {/* The user's own review (hand-written or generated) */}
            {(entity.my_review || entity.my_rating) && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">
                  My Review
                  {entity.my_rating
                    ? ` — ${"★".repeat(entity.my_rating)}${"☆".repeat(5 - entity.my_rating)}`
                    : ""}
                  {entity.vine_reviewed_at
                    ? ` — on Vine ${formatDate(entity.vine_reviewed_at)}`
                    : ""}
                </div>
                <p
                  className="text-secondary"
                  style={{ margin: 0, whiteSpace: "pre-wrap" }}
                >
                  {entity.my_review}
                </p>
              </div>
            )}

            {/* Customer reviews (imported by the browser exporter) */}
            {entity.reviews && (
              <details style={{ marginBottom: "var(--spacing-lg)" }}>
                <summary
                  className="stat-label"
                  style={{ cursor: "pointer", display: "list-item" }}
                >
                  Reviews
                </summary>
                <p
                  className="text-secondary"
                  style={{ margin: 0, whiteSpace: "pre-wrap" }}
                >
                  {entity.reviews}
                </p>
              </details>
            )}

            {/* URL Link */}
            {entity.url && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">Product URL</div>
                <a
                  href={entity.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--spacing-xs)",
                    color: "var(--accent-primary)",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  <LinkIcon size={16} />
                  View on Amazon
                </a>
              </div>
            )}

            {/* Details Section */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "var(--spacing-md)",
                marginBottom: "var(--spacing-lg)",
              }}
            >
              {/* Product fields */}
              {entity.entity_type === "product" && (
                <>
                  {entity.sku && (
                    <div>
                      <div className="stat-label">SKU</div>
                      <div style={{ fontWeight: 500 }}>{entity.sku}</div>
                    </div>
                  )}
                  {entity.quantity !== null &&
                    entity.quantity !== undefined && (
                      <div>
                        <div className="stat-label">Quantity</div>
                        <div style={{ fontWeight: 500 }}>{entity.quantity}</div>
                      </div>
                    )}
                  {entity.condition && (
                    <div>
                      <div className="stat-label">Condition</div>
                      <div style={{ fontWeight: 500 }}>
                        {formatCondition(entity.condition)}
                      </div>
                    </div>
                  )}
                  {entity.purchase_date && (
                    <div>
                      <div className="stat-label">Purchase Date</div>
                      <div style={{ fontWeight: 500 }}>
                        {formatDate(entity.purchase_date)}
                      </div>
                    </div>
                  )}
                  {entity.purchase_price !== null &&
                    entity.purchase_price !== undefined && (
                      <div>
                        <div className="stat-label">Purchase Price</div>
                        <div
                          style={{
                            fontWeight: 500,
                            color:
                              parseFloat(entity.purchase_price) === 0
                                ? "var(--success)"
                                : "inherit",
                          }}
                        >
                          {parseFloat(entity.purchase_price) === 0
                            ? "Free"
                            : `$${parseFloat(entity.purchase_price).toFixed(2)}`}
                        </div>
                      </div>
                    )}
                  {entity.retail_price !== null &&
                    entity.retail_price !== undefined && (
                      <div>
                        <div className="stat-label">Retail Price</div>
                        <div style={{ fontWeight: 500 }}>
                          ${parseFloat(entity.retail_price).toFixed(2)}
                        </div>
                      </div>
                    )}
                  {entity.min_sale_price !== null &&
                    entity.min_sale_price !== undefined && (
                      <div>
                        <div className="stat-label">Min Sale Price</div>
                        <div style={{ fontWeight: 500 }}>
                          ${parseFloat(entity.min_sale_price).toFixed(2)}
                        </div>
                      </div>
                    )}
                  {entity.list_sale_price !== null &&
                    entity.list_sale_price !== undefined && (
                      <div>
                        <div className="stat-label">List Sale Price</div>
                        <div style={{ fontWeight: 500 }}>
                          ${parseFloat(entity.list_sale_price).toFixed(2)}
                        </div>
                      </div>
                    )}
                </>
              )}

              {/* Container fields */}
              {entity.entity_type === "container" && (
                <>
                  {entity.container_type && (
                    <div>
                      <div className="stat-label">Type</div>
                      <div style={{ fontWeight: 500 }}>
                        {formatType(entity.container_type)}
                      </div>
                    </div>
                  )}
                  {entity.capacity !== null &&
                    entity.capacity !== undefined && (
                      <div>
                        <div className="stat-label">Capacity</div>
                        <div style={{ fontWeight: 500 }}>
                          {entity.capacity} items
                        </div>
                      </div>
                    )}
                </>
              )}

              {/* Location fields */}
              {entity.entity_type === "location" && (
                <>
                  {entity.location_type && (
                    <div>
                      <div className="stat-label">Type</div>
                      <div style={{ fontWeight: 500 }}>
                        {formatType(entity.location_type)}
                      </div>
                    </div>
                  )}
                  {entity.address && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div className="stat-label">Address</div>
                      <div style={{ fontWeight: 500 }}>{entity.address}</div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Tags Display */}
            {entity.tags && entity.tags.length > 0 && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">Tags</div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--spacing-xs)",
                    marginTop: "var(--spacing-xs)",
                  }}
                >
                  {entity.tags.map((tag) => (
                    <span
                      key={tag.id}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "var(--radius-sm)",
                        background: `${tag.color}20`,
                        border: `1px solid ${tag.color}`,
                        fontSize: "0.875rem",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: tag.color,
                        }}
                      />
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Location Link */}
            {parent && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">Location</div>
                <div
                  onClick={() => navigate(`/entity/${parent.id}`)}
                  style={{
                    color: "var(--accent-primary)",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {getEntityIcon(parent.entity_type)} {parent.name}{" "}
                  <ChevronRightIcon size={16} />
                </div>
              </div>
            )}

            {/* QR Code URL */}
            {entity.qr_code && (
              <div style={{ marginBottom: "var(--spacing-lg)" }}>
                <div className="stat-label">QR Code URL</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    marginTop: "var(--spacing-xs)",
                  }}
                >
                  <code
                    style={{
                      flex: 1,
                      padding: "var(--spacing-sm) var(--spacing-md)",
                      background: "var(--bg-secondary)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "0.875rem",
                      wordBreak: "break-all",
                    }}
                  >
                    <a
                      href={`${PUBLIC_URL}/scan/${entity.qr_code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--accent-primary)" }}
                    >
                      {`${PUBLIC_URL}/scan/${entity.qr_code}`}
                    </a>
                  </code>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(
                        `${PUBLIC_URL}/scan/${entity.qr_code}`,
                      );
                      setCopiedQrUrl(true);
                      setTimeout(() => setCopiedQrUrl(false), 2000);
                    }}
                    className="btn btn-secondary btn-sm"
                    title="Copy URL"
                  >
                    {copiedQrUrl ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            )}

            {/* Marketplace listing URLs, presented like the QR Code URL */}
            {[
              { name: "eBay", listing: ebayListing },
              { name: "Facebook", listing: facebookListing },
              { name: "OfferUp", listing: offerupListing },
            ]
              .filter(
                (m) =>
                  m.listing?.external_url && m.listing.status === "active",
              )
              .map((m) => (
                <div
                  key={m.name}
                  style={{ marginBottom: "var(--spacing-lg)" }}
                >
                  <div className="stat-label">{m.name} Listing</div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                      marginTop: "var(--spacing-xs)",
                    }}
                  >
                    <code
                      style={{
                        flex: 1,
                        padding: "var(--spacing-sm) var(--spacing-md)",
                        background: "var(--bg-secondary)",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "0.875rem",
                        wordBreak: "break-all",
                      }}
                    >
                      <a
                        href={m.listing.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--accent-primary)" }}
                      >
                        {m.listing.external_url}
                      </a>
                    </code>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(m.listing.external_url);
                        setCopiedListingUrl(m.name);
                        setTimeout(() => setCopiedListingUrl(null), 2000);
                      }}
                      className="btn btn-secondary btn-sm"
                      title="Copy URL"
                    >
                      {copiedListingUrl === m.name ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              ))}

            {/* Action Buttons (products only, hidden once sold or trashed) */}
            {entity.entity_type === "product" && !isSold && !isTrashed && (
              <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                <button
                  onClick={() => setShowMarketplaceSelector(true)}
                  className="btn"
                  style={{
                    flex: 1,
                    background:
                      "linear-gradient(135deg, #00d498 0%, #1877F2 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "var(--spacing-xs)",
                  }}
                >
                  <ShareIcon size={16} />
                  {offerupListing || facebookListing || ebayListing
                    ? "Edit Listings"
                    : "List for Sale"}
                </button>
                <button
                  onClick={() => setShowSellModal(true)}
                  className="btn btn-success"
                  style={{ flex: 1 }}
                >
                  Sell
                </button>
              </div>
            )}

          </div>
        )}

        {/* Children List & Add Item */}
        {!isEditing &&
          (entity.entity_type === "container" ||
            entity.entity_type === "location") && (
            <div className="card">
              {children.length > 0 && (
                <h3 className="mb-2">
                  Contains {children.length}{" "}
                  {children.length === 1 ? "item" : "items"}
                </h3>
              )}
              {children.map((child) => (
                <div
                  key={child.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                    marginBottom: "var(--spacing-xs)",
                  }}
                >
                  <div
                    onClick={() => navigate(`/entity/${child.id}`)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                      flex: 1,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ fontSize: "1.25rem" }}>
                      {getEntityIcon(child.entity_type)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{child.name}</div>
                      <span
                        className={`badge ${getEntityColor(child.entity_type)}`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {child.entity_type}
                      </span>
                    </div>
                    <span style={{ color: "var(--text-muted)" }}>
                      <ChevronRightIcon size={16} />
                    </span>
                  </div>
                  <button
                    onClick={(e) => handleRemoveChild(child, e)}
                    className="btn btn-ghost btn-icon"
                    style={{
                      padding: "4px",
                      minWidth: "auto",
                      color: "var(--text-muted)",
                    }}
                    title="Remove from this location"
                  >
                    <CloseIcon size={16} />
                  </button>
                </div>
              ))}

              {/* Undo bar */}
              {removedChildren.length > 0 && (
                <div
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    background: "var(--bg-tertiary)",
                    borderRadius: "var(--radius-md)",
                    marginBottom: "var(--spacing-xs)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom:
                        removedChildren.length > 1 ? "var(--spacing-xs)" : 0,
                    }}
                  >
                    <span style={{ fontSize: "0.875rem" }}>
                      {removedChildren.length === 1
                        ? `Removed "${removedChildren[0].name}"`
                        : `Removed ${removedChildren.length} items`}
                    </span>
                    {removedChildren.length === 1 ? (
                      <button
                        onClick={() => handleUndoRemove(removedChildren[0])}
                        className="btn btn-ghost"
                        style={{
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          color: "var(--accent-primary)",
                        }}
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        onClick={handleUndoAll}
                        className="btn btn-ghost"
                        style={{
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          color: "var(--accent-primary)",
                        }}
                      >
                        Undo All
                      </button>
                    )}
                  </div>
                  {removedChildren.length > 1 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      {removedChildren.map((child) => (
                        <div
                          key={child.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: "0.8rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          <span>{child.name}</span>
                          <button
                            onClick={() => handleUndoRemove(child)}
                            style={{
                              background: "none",
                              border: "none",
                              padding: "2px 6px",
                              fontSize: "0.8rem",
                              color: "var(--accent-primary)",
                              cursor: "pointer",
                            }}
                          >
                            Undo
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => navigate(`/create?parent=${entity.id}`)}
                className="btn btn-primary"
                style={{
                  width: "100%",
                  marginTop:
                    children.length > 0 || removedChildren.length > 0
                      ? "var(--spacing-md)"
                      : 0,
                }}
              >
                Add Item
              </button>
            </div>
          )}

        {/* Sell Modal */}
        {showSellModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowSellModal(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowSellModal(false);
              }
            }}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Record Sale</h2>
                <button
                  onClick={() => setShowSellModal(false)}
                  className="btn btn-ghost btn-icon"
                >
                  <CloseIcon size={20} />
                </button>
              </div>
              <div className="modal-body">
                <label>Sale Price</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowSellModal(false);
                    } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSell();
                    }
                  }}
                  step="0.01"
                  autoFocus
                />
                {salePrice !== "" && !salePriceValid && (
                  <div
                    style={{
                      marginTop: "var(--spacing-xs)",
                      fontSize: "0.8rem",
                      color: "var(--danger)",
                    }}
                  >
                    Enter a price greater than 0
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowSellModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSell}
                  className="btn btn-success"
                  disabled={!salePriceValid}
                >
                  Confirm Sale
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteConfirm(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowDeleteConfirm(false);
              } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleDelete();
              }
            }}
            tabIndex={0}
            ref={(el) => el?.focus()}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Move to Trash?</h2>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn-ghost btn-icon"
                >
                  <CloseIcon size={20} />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to move <strong>{entity.name}</strong>{" "}
                  to trash?
                </p>
                <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                  You can restore it later from the Trash.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button onClick={handleDelete} className="btn btn-danger">
                  Move to Trash
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Marketplace Selector Modal */}
        {showMarketplaceSelector && entity && (
          <MarketplaceSelectorModal
            product={entity}
            imageUrl={
              mediaItems.find((m) => m.is_primary)
                ? media.getImageUrl(
                    mediaItems.find((m) => m.is_primary).file_path,
                  )
                : null
            }
            existingOfferUpListing={offerupListing}
            existingFacebookListing={facebookListing}
            existingEbayListing={ebayListing}
            onClose={() => setShowMarketplaceSelector(false)}
            onSelectOfferUp={() => {
              setShowMarketplaceSelector(false);
              setShowOfferUpModal(true);
            }}
            onSelectFacebook={() => {
              setShowMarketplaceSelector(false);
              setShowFacebookModal(true);
            }}
            onSelectEbay={() => {
              setShowMarketplaceSelector(false);
              setShowEbayModal(true);
            }}
          />
        )}

        {/* OfferUp Listing Modal */}
        {showOfferUpModal && entity && (
          <OfferUpListingModal
            product={entity}
            imageUrl={
              mediaItems.find((m) => m.is_primary)
                ? media.getImageUrl(
                    mediaItems.find((m) => m.is_primary).file_path,
                  )
                : null
            }
            existingListing={offerupListing}
            onClose={() => setShowOfferUpModal(false)}
            onSuccess={(listing) => {
              if (listing) {
                setOfferupListing(listing);
              } else {
                setOfferupListing(null);
              }
              loadEntity();
            }}
          />
        )}

        {/* Facebook Listing Modal */}
        {showFacebookModal && entity && (
          <FacebookListingModal
            product={entity}
            imageUrl={
              mediaItems.find((m) => m.is_primary)
                ? media.getImageUrl(
                    mediaItems.find((m) => m.is_primary).file_path,
                  )
                : null
            }
            existingListing={facebookListing}
            onClose={() => setShowFacebookModal(false)}
            onSuccess={(listing) => {
              if (listing) {
                setFacebookListing(listing);
              } else {
                setFacebookListing(null);
              }
              loadEntity();
            }}
          />
        )}

        {/* eBay Listing Modal */}
        {showEbayModal && entity && (
          <EbayListingModal
            product={entity}
            imageUrl={
              mediaItems.find((m) => m.is_primary)
                ? media.getImageUrl(
                    mediaItems.find((m) => m.is_primary).file_path,
                  )
                : null
            }
            existingListing={ebayListing}
            onClose={() => setShowEbayModal(false)}
            onSuccess={() => {
              // loadEntity re-fetches the eBay listing record
              loadEntity();
            }}
          />
        )}

        {/* First Sale Congratulations Modal */}
        {showFirstSaleCongrats && (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowFirstSaleCongrats(false);
              navigate("/");
            }}
          >
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{ textAlign: "center", maxWidth: "400px" }}
            >
              <div
                className="modal-body"
                style={{ padding: "var(--spacing-xl)" }}
              >
                <div
                  style={{
                    fontSize: "4rem",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  🎉
                </div>
                <h2
                  style={{
                    fontSize: "1.75rem",
                    marginBottom: "var(--spacing-sm)",
                    background:
                      "linear-gradient(135deg, #00d498 0%, #1877F2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Congratulations!
                </h2>
                <p
                  style={{
                    fontSize: "1.1rem",
                    color: "var(--text-secondary)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  You just made your first sale!
                </p>
                <p
                  style={{
                    fontSize: "0.95rem",
                    color: "var(--text-tertiary)",
                    marginBottom: "var(--spacing-lg)",
                  }}
                >
                  This is just the beginning. Keep listing and selling to grow
                  your business!
                </p>
                <button
                  onClick={() => {
                    setShowFirstSaleCongrats(false);
                    navigate("/");
                  }}
                  className="btn btn-primary"
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-xl)",
                    fontSize: "1rem",
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntityDetail;
